/**
 * A trilha de navegação ("você está aqui") derivada da URL.
 *
 * Existe porque uma tela interna aberta direto — `/admin/implantacoes` — não tinha nenhum
 * caminho de volta na própria página: só o botão do navegador, que não é interface. Derivar
 * da URL, em vez de cada página declarar a sua trilha, garante que rota nova nasça com o
 * caminho de volta em vez de depender de alguém lembrar.
 *
 * Segmento dinâmico é declarado com `:` e casa com qualquer valor, mas **o rótulo continua
 * fixo**: o nome do cliente viria de uma consulta, e uma trilha que às vezes chega vazia
 * pisca na tela. Quem quiser o nome no lugar passa `rotulos` para `montarTrilha`.
 */

export interface DegrauDaTrilha {
  /** Rótulo exibido. */
  titulo: string;
  /** Caminho para onde o degrau leva. O último degrau é a página atual e não vira link. */
  caminho: string;
}

/**
 * Padrão de rota → título.
 *
 * A ordem **não** importa: o casamento é por segmento e `tituloDe` resolve o empate entre
 * padrão literal e padrão com parâmetro sempre a favor do literal. Declarar
 * `/admin/implantacoes/:id` antes de `/admin/implantacoes/nova` não muda nada.
 */
export const TITULOS = [
  ["/app", "Minha implantação"],
  ["/app/formularios", "Formulários"],
  /* O rótulo do segmento dinâmico é fixo de propósito: o nome da trilha viria de uma consulta,
     e uma trilha que às vezes chega vazia pisca na tela. O nome de verdade está no título da
     própria página, que já veio do servidor. */
  ["/app/formularios/:trilha", "Formulário"],
  ["/app/reunioes", "Agenda"],
  ["/app/modulos/:modulo", "Módulo"],
  ["/app/materiais", "Materiais"],
  ["/app/usuarios", "Usuários"],
  ["/app/ativos", "Ativos"],
  ["/app/chamados", "Chamados"],
  ["/app/chamados/:id", "Chamado"],
  ["/app/seguranca", "Segurança da conta"],
  ["/app/chamados/:id/imprimir", "Imprimir"],
  ["/app/ajuda", "Central de ajuda"],
  /* Fixo pelo mesmo motivo do `:trilha`: o título do artigo está na própria página. */
  ["/app/ajuda/:id", "Artigo"],
  ["/app/contrato", "Contrato"],
  ["/app/geoedu", "GEOedu"],
  ["/app/financeiro", "Financeiro"],
  ["/admin", "Console interno"],
  ["/admin/visao-geral", "Visão geral do console"],
  ["/admin/implantacoes", "Implantações"],
  ["/admin/implantacoes/nova", "Nova implantação"],
  ["/admin/implantacoes/:id", "Implantação"],
  ["/admin/implantacoes/:id/kickoff", "Revisão do kickoff"],
  ["/admin/implantacoes/:id/reunioes", "Reuniões de Implantação"],
  /* O rótulo do segmento dinâmico é fixo pelo mesmo motivo do `:trilha`: o tipo e a data da
     reunião viriam de uma consulta, e uma trilha que às vezes chega vazia pisca na tela. */
  ["/admin/implantacoes/:id/reunioes/:reuniao", "Reunião"],
  ["/admin/implantacoes/:id/pacote", "Resumo do Kick Off"],
  ["/admin/implantacoes/:id/cofre", "Cofre de credenciais"],
  ["/admin/catalogo", "Catálogo de Recursos Plus"],
  ["/admin/catalogo-do-kickoff", "Catálogo do kickoff"],
  ["/admin/chamados", "Chamados"],
  ["/admin/chamados/:id", "Chamado"],
  ["/admin/chamados/:id/imprimir", "Imprimir"],
  ["/admin/exportacoes", "Exportações"],
  ["/admin/conhecimento", "Base de conhecimento"],
  ["/admin/conhecimento/novo", "Novo artigo"],
  ["/admin/conhecimento/:id", "Artigo"],
  ["/admin/relatorios", "Relatórios de atendimento"],
  ["/admin/automacao", "Automação"],
  ["/admin/canais", "Canais de entrada"],
  ["/admin/configuracao-de-chamados", "Configurar chamados"],
  ["/admin/horas", "Horas e aprovações"],
  ["/admin/cadastros", "Cadastros"],
  /* Fixo pelo mesmo motivo do `:trilha`: o nome da empresa está no título da própria página. */
  ["/admin/cadastros/organizacoes/:id", "Organização"],
  ["/admin/ativos", "Ativos"],
  ["/admin/avisos", "Mural de avisos"],
  ["/admin/feedback", "Mural de feedback"],
  ["/admin/pesquisas", "Pesquisas de satisfação"],
  ["/admin/sucesso", "Saúde dos clientes"],
  /* Fixo pelo mesmo motivo do `:trilha`: o nome da empresa está no título da própria página. */
  ["/admin/sucesso/:organizacaoId", "Saúde do cliente"],
  ["/admin/compromissos", "Compromissos"],
  ["/admin/migracao", "Migração do Movidesk"],
  ["/admin/paineis", "Painéis da equipe"],
  /* Fixo pelo mesmo motivo do `:trilha`: o nome do painel está no título da própria página. */
  ["/admin/paineis/:id", "Painel"],
  ["/admin/usuarios-internos", "Usuários internos"],
  ["/admin/perfis", "Perfis de acesso"],
  ["/admin/seguranca", "Segurança da conta"],
  ["/admin/webchat", "WebChat"],
  ["/admin/design", "Design system"],
] as const;

/**
 * Os caminhos que têm título — e, por consequência, trilha.
 *
 * O roteador tipa as rotas com sessão por este union. Rota nova sem título registrado
 * **não compila**, que é o que impede a trilha de nascer quebrada por esquecimento; sem
 * isso a promessa "rota nova já vem com o caminho de volta" dependeria de memória.
 */
export type CaminhoComTrilha = (typeof TITULOS)[number][0];

function segmentos(caminho: string): string[] {
  return caminho.split("/").filter(Boolean);
}

function casa(padrao: string, alvo: string): boolean {
  const a = segmentos(padrao);
  const b = segmentos(alvo);
  if (a.length !== b.length) return false;
  return a.every((parte, i) => parte.startsWith(":") || parte === b[i]);
}

function tituloDe(caminho: string): string | undefined {
  // Padrão literal ganha do padrão com parâmetro: `/implantacoes/nova` não pode ser lido
  // como um id chamado "nova".
  const literal = TITULOS.find(([padrao]) => padrao === caminho);
  if (literal) return literal[1];
  return TITULOS.find(([padrao]) => casa(padrao, caminho))?.[1];
}

/**
 * Monta a trilha do caminho atual, do topo da área até a página aberta.
 *
 * Devolve lista vazia quando não há para onde subir — na raiz de uma área a trilha seria
 * um único item apontando para a própria tela, que é ruído, não navegação.
 *
 * @param rotulos substitui o título de um caminho específico (ex.: o nome do cliente).
 */
export function montarTrilha(
  caminho: string,
  rotulos: Readonly<Record<string, string>> = {},
): DegrauDaTrilha[] {
  const partes = segmentos(caminho);
  const degraus: DegrauDaTrilha[] = [];

  for (let i = 0; i < partes.length; i += 1) {
    const parcial = `/${partes.slice(0, i + 1).join("/")}`;
    const titulo = rotulos[parcial] ?? tituloDe(parcial);
    // Segmento sem título registrado é pulado em vez de virar rótulo cru na tela: o
    // caminho de volta que já foi reconhecido continua valendo.
    if (titulo) degraus.push({ titulo, caminho: parcial });
  }

  return degraus.length > 1 ? degraus : [];
}
